import { apiGet, apiPost, apiPatch } from '@/lib/api/client.js';

export type AppointmentRow = {
  id: string;
  appointmentNumber: string;
  customerName: string | null;
  customerPhone: string | null;
  customerEmail: string | null;
  preferredContact: string | null;
  servicesRequested: Array<{ serviceId: string; serviceName: string }>;
  deviceCategory: string | null;
  deviceMake: string | null;
  deviceModel: string | null;
  appointmentDate: string | null;
  startTime: string | null;
  endTime: string | null;
  status: string;
  ticketId: string | null;
  customerNotes: string | null;
  calendarToken: string | null;
};

export type AppointmentSettings = {
  aptBookingEnabled: boolean;
  widgetShowPrices: boolean;
  quoteValidDays: number;
  aptReminderMinutes: number;
  aptCancelFollowupEnabled: boolean;
  aptCancelFollowupDelayMinutes: number;
  aptCancelNotifyManagerSms: boolean;
  aptMinNoticeHours: number;
  aptMaxAdvanceDays: number;
  aptCancellationWindowHours: number;
  aptConfirmationSms: string | null;
  aptReminderSms: string | null;
  aptCancelFollowupSms: string | null;
  quoteSmsTemplate: string | null;
  quoteEmailSubject: string | null;
};

export const appointmentsApi = {
  list: (params?: { date?: string; status?: string; from?: string; to?: string }) =>
    apiGet<AppointmentRow[]>('/api/v1/appointments', { params }).then((r) => r.data),

  todayCount: () =>
    apiGet<{ count: number }>('/api/v1/appointments/today-count').then((r) => r.data.count),

  get: (id: string) => apiGet<AppointmentRow>(`/api/v1/appointments/${id}`).then((r) => r.data),

  create: (body: {
    appointmentDate: string;
    startTime: string;
    customerName: string;
    customerPhone?: string;
    customerEmail?: string;
    preferredContact?: 'sms' | 'email' | 'phone';
    deviceCategory?: string;
    deviceMake?: string;
    deviceModel?: string;
    deviceMakeId?: string;
    deviceModelId?: string;
    serviceIds: string[];
    customerNotes?: string;
  }) =>
    apiPost<{
      appointment: {
        id: string;
        appointmentNumber: string;
        date: string;
        startTime: string;
        endTime: string;
        serviceSummary: string;
      };
    }>('/api/v1/appointments', body).then((r) => r.data),

  checkIn: (id: string) =>
    apiPost<AppointmentRow>(`/api/v1/appointments/${id}/check-in`).then((r) => r.data),

  createTicket: (id: string) =>
    apiPost<{ ticketId: string }>(`/api/v1/appointments/${id}/create-ticket`).then((r) => r.data),

  cancel: (id: string) =>
    apiPost<{ cancelled: boolean }>(`/api/v1/appointments/${id}/cancel`).then((r) => r.data),

  patchStatus: (id: string, status: string) =>
    apiPatch<AppointmentRow>(`/api/v1/appointments/${id}/status`, { status }).then((r) => r.data),

  getSettings: () =>
    apiGet<AppointmentSettings>('/api/v1/appointments/settings/store').then((r) => r.data),

  updateSettings: (body: Partial<AppointmentSettings>) =>
    apiPatch<AppointmentSettings>('/api/v1/appointments/settings/store', body).then((r) => r.data),
};
